import { isEqual } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const getLocalStorageState = <KeysT extends string[]>(keys: KeysT) => {
  return Object.fromEntries(
    keys.map((key) => [key, JSON.parse(localStorage.getItem(key) ?? "null")]),
  ) as Record<KeysT[0], unknown>;
};

export default function useLocalStorage<KeysT extends string[]>({ keys }: { keys: KeysT }) {
  const [state, setState] = useState(getLocalStorageState(keys));

  const setItem = useCallback((key: KeysT[0], value: unknown) => {
    localStorage.setItem(key, JSON.stringify(value));
    window.dispatchEvent(
      new StorageEvent("storage", {
        storageArea: localStorage,
        url: window.location.href,
        key,
      }),
    );
    setState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const keysRef = useRef<string[] | undefined>(undefined);
  useEffect(() => {
    // if keys change, set state
    if (!isEqual(keysRef.current, keys)) {
      keysRef.current = keys;
      setState(getLocalStorageState(keys));
    }
  }, [keys]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.storageArea === localStorage && keysRef.current?.includes(e.key!)) {
        setState((prev) => ({ ...prev, [e.key!]: JSON.parse(e.newValue!) }));
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  return useMemo(
    () => ({
      ...state,
      setItem,
    }),
    [state, setItem],
  );
}
